import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/BuyerRegistrationProfileInfo';
import { BuyerEditRepository } from 'interfaceAdapter/repositories/BuyerEdit';
import { buyerUserContext } from 'application/contexts/useBuyerUser';
import { isNotRegistrationComplete } from 'domain/entities/BuyerUser/BuyerUser';
import { TopOfferBuyersQuery } from 'interfaceAdapter/queries/TopOfferBuyers';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { IPublicBuyer } from 'domain/entities/Buyer/Buyer';
import { useLoading } from 'application/contexts/useLoading';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';

const schema = yup
    .object({
        name: yup.string().required(),
        company_code: yup
            .string()
            .nullable()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です')
            .transform((value) => value || null)
            .default(null),
        target_area: yup.array().of(yup.string()).required(), // 買収希望案件エリア
        target_company_industries: yup.array().of(yup.string()).required(), // 買収希望案件業種
        target_company_employee_size_lower: yup.string().nullable(), // 買収希望案件の従業員数（下限）
        target_company_employee_size_upper: yup.string().nullable(), // 買収希望案件の従業員数（上限）
        target_company_industry_details: yup.string().nullable().max(2000), // 買収希望案件の業種詳細
        target_company_purchase_amount_lower: yup.string().nullable(), // 買収希望金額（下限）
        target_company_purchase_amount_upper: yup.string().nullable(), // 買収希望金額（上限）
        target_company_revenue_scale_lower: yup.string().nullable(), // 買収希望案件の売上高（下限）
        target_company_revenue_scale_upper: yup.string().nullable(), // 買収希望案件の売上高（上限）
        target_company_profit_level_lower: yup.string().nullable(), // 買収希望案件の営業利益（下限）
        target_company_profit_level_upper: yup.string().nullable(), // 買収希望案件の営業利益（上限）
        target_company_net_assets_level_lower: yup.string().nullable(), // 買収希望案件の純資産金額（下限）
        target_company_net_assets_level_upper: yup.string().nullable(), // 買収希望案件の純資産金額（上限）
        has_acquisition_experience: yup.string().nullable(), // 過去の買収実績があるか
        other_buying_points: yup.string().nullable().max(1000), // その他希望事項等
        number_of_employees: yup.string(), // 自社の従業員数
        revenue_scale: yup.string(), // 自社の売上規模
        business_description: yup.string().max(1000), // 自社の事業内容
        question: yup.string().nullable().max(1000), // 仲介会社に聞いてみたいこと
        icon_image: yup.string().nullable(),
        hp_url: yup.string().nullable(),
        fee_structure: yup.boolean().required(), // M&Aに関する手数料水準について
        other_buyers: yup.boolean().required(), // 他の買い手候補について
        contract_status: yup.boolean().required(), // 提案案件の契約状況について
        process_flow: yup.boolean().required(), // 具体的な流れについて
        business_synergy: yup.boolean().required(), // 貴社の想定する事業シナジー、事業展望、スキーム等について
        support_system: yup.boolean().required(), // 支援体制、サポート内容について
        fee_structure_txt: yup.string().default('').max(1000), // M&Aに関する手数料水準について（詳細）
        other_buyers_txt: yup.string().default('').max(1000), // 他の買い手候補について（詳細）
        contract_status_txt: yup.string().default('').max(1000), // 提案案件の契約状況について（詳細）
        process_flow_txt: yup.string().default('').max(1000), // 具体的な流れについて（詳細）
        business_synergy_txt: yup.string().default('').max(1000), // 貴社の想定する事業シナジー、事業展望、スキーム等について（詳細）
        support_system_txt: yup.string().default('').max(1000), // 支援体制、サポート内容について（詳細）
        is_sole_proprietor: yup.boolean().required(),
    })
    .required();

type BuyerForm = yup.InferType<typeof schema>;

export const useBuyerEdit = () => {
    const diContainer = useContext(diContainerContext);
    const buyerRegistrationProfileInfoQuery = diContainer.resolve(BuyerRegistrationProfileInfoQuery);
    const buyerEditRepository = diContainer.resolve(BuyerEditRepository);
    const topOfferBuyersQuery = diContainer.resolve(TopOfferBuyersQuery);
    const { user } = useContext(buyerUserContext);
    const notRegistrationComplete = isNotRegistrationComplete(user);
    const { loading, setLoading } = useLoading();
    const [editing, setEditing] = useState(notRegistrationComplete);
    const [areaChoices, setAreaChoices] = useState<IAreaOption[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);
    const [purchaseAmountLowerChoices, setPurchaseAmountLowerChoices] = useState<string[][]>([]);
    const [purchaseAmountUpperChoices, setPurchaseAmountUpperChoices] = useState<string[][]>([]);
    const [revenueScaleLowerChoices, setRevenueScaleLowerChoices] = useState<string[][]>([]);
    const [revenueScaleUpperChoices, setRevenueScaleUpperChoices] = useState<string[][]>([]);
    const [profitLevelLowerChoices, setProfitLevelLowerChoices] = useState<string[][]>([]);
    const [profitLevelUpperChoices, setProfitLevelUpperChoices] = useState<string[][]>([]);
    const [employeeLowerChoices, setEmployeeLowerChoices] = useState<string[][]>([]);
    const [employeeUpperChoices, setEmployeeUpperChoices] = useState<string[][]>([]);
    const [netAssetsLevelLowerChoices, setNetAssetsLevelLowerChoices] = useState<string[][]>([]);
    const [netAssetsLevelUpperChoices, setNetAssetsLevelUpperChoices] = useState<string[][]>([]);
    const [hasAcquisitionExperienceChoices, setHasAcquisitionExperienceChoices] = useState<string[][]>([]);
    const [companies, setCompanies] = useState<IPublicBuyer[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackBarMessage, setSnackbarMessage] = useState('');

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<BuyerForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    // useEffect(() => {
    //     setLoading(true);
    //     const fetchData = async () => {
    //         try {
    //             const resCompanies = await topOfferBuyersQuery.fetchList();
    //             setCompanies(resCompanies);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchData();
    // }, []);

    useEffect(() => {
        const fetchData = async () => {
            const data = await buyerRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(data.industry_choices);
            setPurchaseAmountLowerChoices(data.purchase_amount_lower_choices);
            setPurchaseAmountUpperChoices(data.purchase_amount_upper_choices);
            setRevenueScaleLowerChoices(data.revenue_scale_lower_choices);
            setRevenueScaleUpperChoices(data.revenue_scale_upper_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setEmployeeLowerChoices(data.employee_lower_choices);
            setEmployeeUpperChoices(data.employee_upper_choices);
            setNetAssetsLevelLowerChoices(data.net_assets_level_lower_choices);
            setNetAssetsLevelUpperChoices(data.net_assets_level_upper_choices);
            setHasAcquisitionExperienceChoices(data.has_acquisition_experience_choices);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (user?.buyer) {
            const area = Array.isArray(user.buyer.area) ? user.buyer.area : (user.buyer.area as string).split(',');
            setValue('name', user.buyer.name);
            setValue('company_code', user.buyer.company_code);
            setValue('icon_image', user.buyer.icon_image);
            setValue('target_area', area);
            setValue('target_company_industries', user.buyer.target_company_industries);
            setValue('target_company_employee_size_lower', user.buyer.target_company_employee_size_lower);
            setValue('target_company_employee_size_upper', user.buyer.target_company_employee_size_upper);
            setValue('target_company_revenue_scale_lower', user.buyer.target_company_revenue_scale_lower);
            setValue('target_company_revenue_scale_upper', user.buyer.target_company_revenue_scale_upper);
            setValue('target_company_profit_level_lower', user.buyer.target_company_profit_level_lower);
            setValue('target_company_profit_level_upper', user.buyer.target_company_profit_level_upper);
            setValue('target_company_net_assets_level_lower', user.buyer.target_company_net_assets_level_lower);
            setValue('target_company_net_assets_level_upper', user.buyer.target_company_net_assets_level_upper);
            setValue('has_acquisition_experience', user.buyer.has_acquisition_experience);
            setValue('target_company_industry_details', user.buyer.target_company_industry_details);
            setValue('target_company_purchase_amount_lower', user.buyer.target_company_purchase_amount_lower);
            setValue('target_company_purchase_amount_upper', user.buyer.target_company_purchase_amount_upper);
            setValue('target_company_revenue_scale_lower', user.buyer.target_company_revenue_scale_lower);
            setValue('target_company_revenue_scale_upper', user.buyer.target_company_revenue_scale_upper);
            setValue('target_company_profit_level_lower', user.buyer.target_company_profit_level_lower);
            setValue('target_company_profit_level_upper', user.buyer.target_company_profit_level_upper);
            setValue('other_buying_points', user.buyer.other_buying_points);
            setValue('number_of_employees', user.buyer.number_of_employees);
            // TODO:「99:5億円～」は旧選択肢保持の為、除外。該当デフォルトデータがなくなれば削除
            setValue('revenue_scale', user.buyer.revenue_scale !== '99' ? user.buyer.revenue_scale : '');
            setValue('fee_structure', user.buyer.fee_structure);
            setValue('other_buyers', user.buyer.other_buyers);
            setValue('contract_status', user.buyer.contract_status);
            setValue('process_flow', user.buyer.process_flow);
            setValue('business_synergy', user.buyer.business_synergy);
            setValue('support_system', user.buyer.support_system);
            setValue('fee_structure_txt', user.buyer.fee_structure_txt || '');
            setValue('other_buyers_txt', user.buyer.other_buyers_txt || '');
            setValue('contract_status_txt', user.buyer.contract_status_txt || '');
            setValue('process_flow_txt', user.buyer.process_flow_txt || '');
            setValue('business_synergy_txt', user.buyer.business_synergy_txt || '');
            setValue('support_system_txt', user.buyer.support_system_txt || '');
            setValue('business_description', user.buyer.business_description);
            setValue('question', user.buyer.question);
            setValue('hp_url', user.buyer.hp_url);
            setValue('is_sole_proprietor', user.buyer.is_sole_proprietor);
        }
    }, [setValue, user]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await buyerEditRepository.editProfile({
                ...data,
            });
            setSnackbarMessage('企業プロフィールを更新しました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/buyer/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    const unPublish = async () => {
        setLoading(true);
        try {
            await buyerEditRepository.unPublish();
            setSnackbarMessage('企業プロフィールを非公開にしました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/buyer/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    };

    const reOpen = async () => {
        setLoading(true);
        try {
            await buyerEditRepository.reOpen();
            setSnackbarMessage('企業プロフィールを公開しました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/buyer/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    };

    const onCompanySelect = (company: CompanySearchResult) => {
        setValue('name', company.name);
        setValue('company_code', company.number);
    };

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        notRegistrationComplete,
        hasAcquisitionExperienceChoices,
        editing,
        user,
        companies,
        snackBarMessage,
        onCompanySelect,
        setEditing,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
        unPublish,
        reOpen,
    };
};
